/**
 * Instructor accounts — the studio owner's screen.
 *
 * An instructor is an account, not just a name: they sign in with their own
 * password and see their own students. Creating one therefore means creating a
 * login, which is why this screen is the owner's alone and why every action on
 * it asks for the studio password again.
 *
 * There is no email in the MVP, so a password reaches an instructor the only
 * way it can: the owner reads it out. It is a six-digit PIN the server
 * generates, shown here once, and the instructor never changes it — the owner
 * resets it when it is forgotten (PRD §3.2.1).
 */

import { useState } from 'react';
import { toUsername } from '@pp/shared';
import { ApiError, api } from '../api/client.js';
import { messageFor } from '../api/error-messages.js';
import { AdminReauthDialog } from '../components/ui/AdminReauthDialog.js';
import { Button } from '../components/ui/Button.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { Field } from '../components/ui/Field.js';
import { useApiData } from '../hooks/useApiData.js';
import { useSession } from '../session/SessionContext.js';
import './Instructors.css';

interface Instructor {
  id: string;
  name: string;
  status: 'active' | 'inactive';
  username?: string | null;
  hasAccount?: boolean;
}

export function Instructors() {
  const { session, hasAdminAccess } = useSession();
  const { data, error, loading, reload } = useApiData<{ items: Instructor[] }>(
    '/api/instructors?status=all',
  );

  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [usernameEdited, setUsernameEdited] = useState(false);
  const [formError, setFormError] = useState<string>();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const [resetting, setResetting] = useState<Instructor | null>(null);
  const [confirmOff, setConfirmOff] = useState<Instructor | null>(null);
  const [reauthFor, setReauthFor] = useState<null | (() => void)>(null);
  const [issued, setIssued] = useState<{ name: string; pin: string } | null>(null);

  // Active first: a studio that has been running a while accumulates people
  // who have left, and they should not push the working list off the screen.
  const items = [...(data?.items ?? [])].sort((a, b) =>
    a.status === b.status ? 0 : a.status === 'active' ? -1 : 1,
  );

  if (session && session.role !== 'owner') {
    return (
      <section className="instructors">
        <h1 className="page__title">Eğitmenler</h1>
        <p className="instructors__denied">
          Eğitmen hesapları yalnızca salon yöneticisi tarafından yönetilir.
        </p>
      </section>
    );
  }

  /** Every action here needs the studio password within the last ten minutes. */
  function withAdmin(action: () => void) {
    if (hasAdminAccess()) action();
    else setReauthFor(() => action);
  }

  async function create(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setFormError(undefined);
    setFieldErrors({});
    try {
      // The PIN is generated by the server and comes back once.
      const created = await api.post<{ pin: string }>('/api/instructors', {
        name,
        username,
      });
      setIssued({ name, pin: created.pin });
      setAdding(false);
      setName('');
      setUsername('');
      setUsernameEdited(false);
      reload();
    } catch (err) {
      // "Check the information you entered" with nothing marked is not an
      // error message; the API names the fields, so the form points at them.
      if (err instanceof ApiError) {
        const fields = err.details?.fields;
        if (Array.isArray(fields)) {
          setFieldErrors(
            Object.fromEntries(
              fields.map((field) => [
                String(field),
                field === 'username'
                  ? 'Bu kullanıcı adı zaten kullanılıyor. Başka bir tane deneyin.'
                  : 'Bu alanı kontrol edin.',
              ]),
            ),
          );
        }
        setFormError(messageFor(err.code));
      } else setFormError(messageFor('INTERNAL_ERROR'));
    } finally {
      setBusy(false);
    }
  }

  async function resetFor(instructor: Instructor) {
    try {
      const result = await api.post<{ pin: string }>(
        `/api/instructors/${instructor.id}/password`,
      );
      setIssued({ name: instructor.name, pin: result.pin });
      setResetting(null);
      reload();
    } catch (err) {
      setFormError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    }
  }

  async function deactivate(instructor: Instructor) {
    setConfirmOff(null);
    try {
      await api.delete(`/api/instructors/${instructor.id}`);
      reload();
    } catch (err) {
      setFormError(
        err instanceof ApiError ? messageFor(err.code) : messageFor('INTERNAL_ERROR'),
      );
    }
  }

  return (
    <section className="instructors">
      <div className="instructors__head">
        <h1 className="page__title">Eğitmenler</h1>
        {!adding && (
          <Button type="button" onClick={() => withAdmin(() => setAdding(true))}>
            Eğitmen Ekle
          </Button>
        )}
      </div>

      <p className="instructors__hint">
        Her eğitmen kendi kullanıcı adı ve şifresiyle girer ve{' '}
        <b>yalnızca kendi öğrencilerini</b> görür. Siz, salon yöneticisi olarak hepsini
        görürsünüz.
      </p>

      {(error || formError) && <p role="alert">{error ?? formError}</p>}

      {/* Shown once. There is no way to read it back — only to reset it. */}
      {issued && (
        <div className="instructors__issued" role="status">
          <p>
            <b>{issued.name}</b> için giriş şifresi:
          </p>
          <code className="instructors__password">{issued.pin}</code>
          <p className="instructors__issued-note">
            Bu şifreyi eğitmene siz iletin. Kalıcı şifresidir, değiştirmesi gerekmez. Bir
            daha görüntülenemez — unutulursa buradan sıfırlarsınız.
          </p>
          <Button type="button" variant="secondary" onClick={() => setIssued(null)}>
            Anladım
          </Button>
        </div>
      )}

      {adding && (
        <form onSubmit={create} className="instructors__form" noValidate>
          <Field
            label="Ad Soyad"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              // Suggested, not imposed: the owner may still type their own.
              if (!usernameEdited) setUsername(toUsername(e.target.value));
            }}
            error={fieldErrors.name}
            required
          />
          <Field
            label="Kullanıcı adı"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            hint="Eğitmen girişte bunu yazacak. Türkçe harfler sadeleştirilir: “Ayşe” → “ayse”."
            value={username}
            onChange={(e) => {
              // Folded as it is typed, so an invalid username cannot be
              // entered and the owner sees what the instructor will type.
              setUsernameEdited(true);
              setUsername(toUsername(e.target.value));
            }}
            error={fieldErrors.username}
            required
          />
          <p className="instructors__form-note">
            Şifre gerekmiyor: kaydettiğinizde 6 haneli bir şifre üretilecek ve size bir
            kez gösterilecek.
          </p>

          <div className="instructors__actions">
            <Button type="button" variant="secondary" onClick={() => setAdding(false)}>
              Vazgeç
            </Button>
            <Button
              type="submit"
              loading={busy}
              disabled={name.trim() === '' || username.length < 2}
            >
              Kaydet
            </Button>
          </div>
        </form>
      )}

      {loading && <p className="profile__muted">Yükleniyor…</p>}

      {!loading && items.length === 0 && !adding && (
        <div className="profile__empty">
          <p>Henüz eğitmen eklenmemiş. Öğrenci eklemeden önce bir eğitmen ekleyin.</p>
          <Button type="button" onClick={() => withAdmin(() => setAdding(true))}>
            Eğitmen Ekle
          </Button>
        </div>
      )}

      {items.length > 0 && (
        <ul className="instructors__list">
          {items.map((instructor) => (
            <li
              key={instructor.id}
              className={
                instructor.status === 'active'
                  ? 'instructors__card'
                  : 'instructors__card instructors__card--off'
              }
            >
              <div className="instructors__identity">
                <span className="instructors__name">{instructor.name}</span>
                <span className="instructors__meta">
                  {instructor.username
                    ? `Kullanıcı adı: ${instructor.username}`
                    : 'Girişi yok'}
                  {instructor.status === 'inactive' && ' · Pasif'}
                </span>
              </div>

              {instructor.status === 'active' && (
                <div className="instructors__row-actions">
                  <button
                    type="button"
                    className="instructors__link"
                    onClick={() => withAdmin(() => setResetting(instructor))}
                  >
                    Şifre Sıfırla
                  </button>
                  <button
                    type="button"
                    className="instructors__link instructors__link--off"
                    onClick={() => withAdmin(() => setConfirmOff(instructor))}
                  >
                    Pasife Al
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {resetting && (
        <ConfirmDialog
          title={`${resetting.name} için şifre sıfırlansın mı?`}
          body="Yeni 6 haneli şifre üretilir ve size bir kez gösterilir. Eğitmenin açık oturumları kapanır; eski şifresi çalışmaz."
          confirmLabel="Sıfırla"
          onCancel={() => setResetting(null)}
          onConfirm={() => void resetFor(resetting)}
        />
      )}

      {confirmOff && (
        <ConfirmDialog
          title={`${confirmOff.name} pasife alınsın mı?`}
          body="Girişi kapanır ve açık oturumları sona erer. Öğrencileri, ölçümleri ve raporları silinmez — onları başka bir eğitmene siz aktarırsınız."
          confirmLabel="Pasife Al"
          destructive
          onCancel={() => setConfirmOff(null)}
          onConfirm={() => void deactivate(confirmOff)}
        />
      )}

      {reauthFor && (
        <AdminReauthDialog
          action="Eğitmen hesaplarını yönetmek"
          onCancel={() => setReauthFor(null)}
          onConfirmed={() => {
            const action = reauthFor;
            setReauthFor(null);
            action();
          }}
        />
      )}
    </section>
  );
}
